import { mapApprover } from '@webapp/core/data-access/mappers/mapApprover'
import { type PrimaryCategoryConstructor } from '@webapp/core/entities/internal/PrimaryCategory'
import { type SecondaryCategoryConstructor } from '@webapp/core/entities/internal/SecondaryCategory'
import { formatRepoFiltersToApiFilters } from '@webapp/core/utils/repositories/formatRepoFiltersToApiFilters'
import { throwRepoErrorFromApiV3Error } from '@webapp/core/utils/repositories/throwRepoErrorFromApiV3Error'
import { type RepoFilterParams } from '@webapp/infrastructure/interfaces/repositories/RepoFilterParams'
import { type RepoPaginatedResponse } from '@webapp/infrastructure/interfaces/repositories/RepoPaginatedResponse'
import { type RepoStandardResponse } from '@webapp/infrastructure/interfaces/repositories/RepoStandardResponse'
import { type IHttpClient } from '@webapp/infrastructure/interfaces/services/IHttpClient'
import { type IPersistentStorage } from '@webapp/infrastructure/interfaces/services/IPersistentStorage'
import { type IOrderDraftRepository } from '@webapp/modules/purchasing/business-logic/interfaces/IOrderDraftRepository'
import { type CreateAutomationOrderDraftData } from '@webapp/modules/purchasing/business-logic/types/CreateAutomationOrderDraftData'
import { type GetNextApprovalStepData } from '@webapp/modules/purchasing/business-logic/types/GetNextApprovalStepData'
import { type NextApprovalStepResponse } from '@webapp/modules/purchasing/business-logic/types/NextApprovalStepResponse'
import { type SubmitOrderDraftData } from '@webapp/modules/purchasing/business-logic/types/SubmitOrderDraftData'
import { type UpdateAutomationOrderDraftData } from '@webapp/modules/purchasing/business-logic/types/UpdateAutomationOrderDraftData'
import { type UpdateOrderDraftData } from '@webapp/modules/purchasing/business-logic/types/UpdateOrderDraftData'
import { mapOrderDraftComputedValues } from '@webapp/modules/purchasing/data-access/mappers/mapOrderDraftComputedValues'
import { type GetOrderDraftComputedValuesResponse } from '@webapp/modules/purchasing/data-access/types/GetOrderDraftComputedValuesResponse'
import { type GetOrderDraftPreferredCreationFlowResponse } from '@webapp/modules/purchasing/data-access/types/GetOrderDraftPreferredCreationFlowResponse'
import { OrderStatuses } from '@webapp/modules/purchasing/entities/enums/OrderStatusEnum'
import { type OrderDraftConstructorData } from '@webapp/modules/purchasing/entities/OrderDraft'
import { type OrderDraftItemConstructorData } from '@webapp/modules/purchasing/entities/OrderDraftItem'
import { type SubmitOrderDraftResponse } from '../../../business-logic/types/SubmitOrderDraftResponse'
import { type LegacyOrderDraftConstructorData } from '../../../entities/LegacyOrderDraft/LegacyOrderDraft'
import { mapLegacyOrderDraftApiModel } from '../../mappers/mapLegacyOrderDraftApiModel'
import { mapOrderDraftApiModel } from '../../mappers/mapOrderDraftApiModel'
import { mapOrderDraftItemApiModel } from '../../mappers/mapOrderDraftItemApiModel'
import { type GetLegacyOrderDraftResponse } from '../../types/GetLegacyOrderDraftResponse'
import { type GetOrderDraftResponse } from '../../types/GetOrderDraftResponse'
import { type GetRequestablePrimaryCategoryResponse } from '../../types/GetRequestablePrimaryCategoryResponse'
import { type GetRequestableSecondaryCategoryResponse } from '../../types/GetRequestableSecondaryCategoryResponse'
import { type PatchAutomationOrderDraftPayload } from '../../types/PatchAutomationOrderDraftPayload'
import { type PostCreateOrderFromDraftPayload } from '../../types/PostCreateOrderFromDraftPayload'
import { type PostCreateOrderFromDraftResponse } from '../../types/PostCreateOrderFromDraftResponse'
import { type PostInitialApproversPayload } from '../../types/PostInitialApproversPayload'
import { type PostInitialApproversResponse } from '../../types/PostInitialApproversResponse'
import { type PutOrderDraftPayload } from '../../types/PutOrderDraftPayload'
import { type PutSubmitOrderDraftResponse } from '../../types/PutSubmitOrderDraftResponse'
import { type SetOrderDraftPreferredCreationFlowData } from '../../types/SetOrderDraftPreferredCreationFlowData'

interface Dependency {
  httpClient: IHttpClient
  persistentStorage: IPersistentStorage
}

export class OrderDraftRepository implements IOrderDraftRepository {
  constructor(private readonly deps: Dependency) {}

  private getOrderDraftCreationFlowPreferenceStorageKey(
    userId: number
  ): string {
    return `procurify.order-request-creation-flow-preference_${userId}`
  }

  async submitOrderDraft(
    data: SubmitOrderDraftData,
    options?: { confirmedBudgetOverage?: boolean }
  ): Promise<RepoStandardResponse<SubmitOrderDraftResponse>> {
    try {
      const {
        orderId,
        nextApprovers,
        dateRequired,
        primaryCategoryId,
        secondaryCategoryId,
        description,
      } = data
      const res = await this.deps.httpClient.put<PutSubmitOrderDraftResponse>(
        `/api/v4/orders/${orderId}/`,
        {
          status: OrderStatuses.PENDING, // Transition from DRAFT to PENDING implies submitting RFO for approval
          next_approvers: nextApprovers,
          date_required: dateRequired,
          location: primaryCategoryId,
          department: secondaryCategoryId,
          description,
        },
        {
          params: {
            ...(options?.confirmedBudgetOverage && {
              confirmed_budget_overage: true,
            }),
          },
        }
      )

      const { data: orderResponseData } = res.data

      return {
        data: {
          orderUuid: orderResponseData.uuid,
          orderNumber: orderResponseData.order_number ?? orderResponseData.num,
        },
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  // Note: Calling this endpoint will also initiate the processing of the attachment by the agentic system.
  async attachFileToAutomationOrderDraft(attachmentId: string): Promise<void> {
    try {
      await this.deps.httpClient.post(
        '/api/v3/automation/order-draft/attachments/',
        {
          attachments: [attachmentId],
        }
      )
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  // Note: Calling this endpoint will also initiate the processing of the attachment by the agentic system (quote extraction).
  async populateOrderDraftFromAttachment(attachmentId: string): Promise<void> {
    try {
      await this.deps.httpClient.post('/api/v4/orders/ai-draft/', {
        attachment: attachmentId,
      })
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async createOrderDraft(
    secondaryCategoryId?: Nullable<number>
  ): Promise<OrderDraftConstructorData> {
    try {
      const res = await this.deps.httpClient.post<GetOrderDraftResponse>(
        '/api/v4/orders/',
        {
          status: OrderStatuses.DRAFT,
          ...(secondaryCategoryId && { department: secondaryCategoryId }),
        }
      )

      return mapOrderDraftApiModel(res.data.data, null)
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async createOrderFromAutomationOrderDraft(
    data: CreateAutomationOrderDraftData
  ): Promise<{
    orderNum: number
    orderNumber: number
    orderUuid: uuid
  }> {
    try {
      const res = await this.deps.httpClient.post<
        { data: PostCreateOrderFromDraftResponse },
        PostCreateOrderFromDraftPayload
      >(
        '/api/v3/automation/order-cart/submit/?confirmed_budget_overture=false',
        {
          location: data.primaryCategoryId,
          department: data.secondaryCategoryId,
          next_approvers: data.nextApprovers,
          dateRequired: data.dateRequired,
          description: data.description,
        }
      )

      const { data: responseData } = res.data

      return {
        orderNum: responseData.order_num,
        orderNumber: responseData.order_number ?? responseData.order_num,
        orderUuid: responseData.uuid,
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async deleteAutomationOrderDraft(): Promise<void> {
    try {
      await this.deps.httpClient.delete('/api/v3/automation/order-draft/')
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async getAutomationOrderDraft(): Promise<LegacyOrderDraftConstructorData> {
    try {
      const res = await this.deps.httpClient.get<GetLegacyOrderDraftResponse>(
        '/api/v3/automation/order-draft/'
      )

      const data = res.data.data

      return mapLegacyOrderDraftApiModel(data)
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async getOrderDraft(): Promise<OrderDraftConstructorData> {
    try {
      const res = await this.deps.httpClient.get<GetOrderDraftResponse>(
        '/api/v4/orders/draft/'
      )

      return mapOrderDraftApiModel(
        res.data.data,
        res.data.metadata?.ai_extraction_status ?? null
      )
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async getOrderDraftComputedValues(): Promise<GetOrderDraftComputedValuesResponse> {
    try {
      const res = await this.deps.httpClient.get<GetOrderDraftResponse>(
        '/api/v4/orders/draft/'
      )

      return mapOrderDraftComputedValues(res.data.data)
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async getInitialApproverOptions(
    data: GetNextApprovalStepData
  ): Promise<NextApprovalStepResponse> {
    try {
      const res = await this.deps.httpClient.post<
        PostInitialApproversResponse,
        PostInitialApproversPayload
      >('/api/v3/approval_routing/initial_approvers/', {
        account_code_ids: data.accountCodeIds,
        amount: data.amount,
        base_content_type_id: data.baseContentTypeId,
        custom_fields: data.customFields,
        department: data.department,
      })

      const { data: responseData } = res.data

      return {
        canPeerReview: responseData.can_peer_review,
        options: responseData.options.map((option) => ({
          approvers: option.approvers.map((approver) => mapApprover(approver)),
          isPooled: option.is_pooled,
          triggersFinalApproval: option.triggers_final_approval,
          level: option.level,
          groupId: option.group_id,
          groupName: option.group_name,
        })),
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async getOrderDraftPreferredCreationFlow(
    userId: number
  ): Promise<GetOrderDraftPreferredCreationFlowResponse> {
    const storageKey =
      this.getOrderDraftCreationFlowPreferenceStorageKey(userId)

    const preferredCreationFlow: GetOrderDraftPreferredCreationFlowResponse =
      await this.deps.persistentStorage.get(storageKey)

    return preferredCreationFlow
  }

  async getRequestablePrimaryCategory(
    userId: number,
    params?: RepoFilterParams
  ): Promise<RepoPaginatedResponse<PrimaryCategoryConstructor>> {
    try {
      const res =
        await this.deps.httpClient.get<GetRequestablePrimaryCategoryResponse>(
          '/api/v3/locations/',
          {
            params: {
              ...formatRepoFiltersToApiFilters({
                orderBy: 'name',
                ...params,
              }),
              requestable_user: userId,
              requestable: 'ORDER',
              simple: true,
            },
          }
        )

      const data = res.data.data
      const metadata = res.data.metadata

      return {
        data: data.map((d) => ({
          id: d.id,
          name: d.name,
        })),
        pagination: {
          count: metadata?.pagination?.count ?? 0,
          page: metadata?.pagination?.current_page ?? 0,
          pageSize: metadata?.pagination?.page_size ?? 0,
          numPages: metadata?.pagination?.num_pages ?? 0,
        },
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async getRequestableSecondaryCategories(
    userId: number,
    primaryCategoryId: number,
    params?: RepoFilterParams
  ): Promise<RepoPaginatedResponse<SecondaryCategoryConstructor>> {
    try {
      const res =
        await this.deps.httpClient.get<GetRequestableSecondaryCategoryResponse>(
          '/api/v3/departments/',
          {
            params: {
              branch: primaryCategoryId,
              ...formatRepoFiltersToApiFilters({
                orderBy: 'name',
                ...params,
              }),
              requestable_user: userId,
              requestable: 'ORDER',
              simple: true,
            },
          }
        )

      const data = res.data.data
      const metadata = res.data.metadata

      return {
        data: data.map((d) => ({
          id: d.id,
          name: d.name,
          primaryCategory: {
            id: d.branch.id,
            name: d.branch.name,
          },
        })),
        pagination: {
          count: metadata?.pagination?.count ?? 0,
          page: metadata?.pagination?.current_page ?? 0,
          pageSize: metadata?.pagination?.page_size ?? 0,
          numPages: metadata?.pagination?.num_pages ?? 0,
        },
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  setOrderDraftPreferredCreationFlow(
    userId: number,
    preferredFlow: SetOrderDraftPreferredCreationFlowData
  ): void {
    try {
      const storageKey =
        this.getOrderDraftCreationFlowPreferenceStorageKey(userId)
      this.deps.persistentStorage.set(storageKey, preferredFlow)
    } catch (error) {
      throw throwRepoErrorFromApiV3Error(error)
    }
  }

  async updateAutomationOrderDraft(
    data: UpdateAutomationOrderDraftData
  ): Promise<void> {
    try {
      await this.deps.httpClient.patch<void, PatchAutomationOrderDraftPayload>(
        '/api/v3/automation/order-draft/',
        {
          ...(data.dateRequired !== undefined && {
            dateRequired: data.dateRequired,
          }),
          ...(data.primaryCategoryId !== undefined && {
            location: data.primaryCategoryId,
          }),
          ...(data.secondaryCategoryId !== undefined && {
            department: data.secondaryCategoryId,
          }),
          ...(data.description !== undefined && {
            description: data.description,
          }),
        }
      )
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async updateOrderDraft(
    orderId: number,
    data: UpdateOrderDraftData
  ): Promise<OrderDraftItemConstructorData[]> {
    try {
      const res = await this.deps.httpClient.put<
        GetOrderDraftResponse,
        PutOrderDraftPayload
      >(`/api/v4/orders/${orderId}/`, {
        status: data.status,
        description: data.description,
        date_required: data.dateRequired,
        location: data.primaryCategoryId,
        department: data.secondaryCategoryId,
        next_approvers: data.nextApprovers,
      })

      // Return items so callers can re-link accounts after a category change.
      const items = res.data.data.order_items?.map((orderItem) =>
        mapOrderDraftItemApiModel(orderItem)
      )

      return items ?? []
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }
}
